import { debounce } from 'lodash';
import { CloudOff, Loader2, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { OrgLogoId, OrgLogos } from '@crossnokaye/ux-animatics';
import {
  cn,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  NotificationBadge,
} from '@crossnokaye/ux-common';

import { useFilteredOrgFacilities } from '@/components/navigation/hooks/use-filtered-org-facilities';
import { useOfflineFacilities } from '@/components/navigation/hooks/use-offline-facilities';
import { NavTooltipDropdownTrigger } from '@/components/navigation/left/components/shared/nav-tooltip-dropdown-trigger';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useNavStore } from '@/stores/nav';
import {
  Input,
  InputClear,
  InputField,
  InputLeadingIcon,
} from '@/ux-common/components/input/input';

export const FacilityMoreMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { enableFacilityMenu, orgIcon, setFacilitySearchQuery, facilitySearchQuery } =
    useNavStore();

  const { offlineFacilities, getOfflineFacilityCountByOrg } = useOfflineFacilities();

  const { filteredOrgFacilityList } = useFilteredOrgFacilities();

  const { facilityId: currentFacilityId } = useCurrentFacility();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setInputValue(facilitySearchQuery);
    }
  }, [isOpen, facilitySearchQuery]);

  const handleSelect = (e: React.MouseEvent, orgName: string, shortName: string) => {
    e.stopPropagation();

    navigate({
      to: '.',
      params: (prev) => ({ ...prev, org: orgName, agent: shortName }),
    });
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFacilitySearchQuery(value);
      }, 300),
    [setFacilitySearchQuery]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    debouncedSearch(newValue);
  };

  const handleClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue('');
    setFacilitySearchQuery('');
  };
  const OrgLogoIcon = OrgLogos[orgIcon as OrgLogoId];

  return (
    <div>
      <NavTooltipDropdownTrigger
        dropdownTriggerProps={{
          disabled: !enableFacilityMenu,
          onClick: (e) => e.stopPropagation(),
        }}
        label={'Click to View Facility Picker'}
        menuOpen={isOpen}
        onMenuOpenChange={setIsOpen}
        tooltipDisabled={!enableFacilityMenu}
        trigger={
          <div
            className={cn(
              'flex justify-center bg-popover w-full py-2.5 my-2',
              enableFacilityMenu && 'cursor-pointer hover:bg-tertiary',
              isOpen && 'bg-tertiary'
            )}
            data-testid={'collapsed-nav-facility-selector'}
          >
            {OrgLogoIcon ? (
              <OrgLogoIcon size={'2rem'} color={'white'} />
            ) : (
              <Loader2
                className="glyph glyph-sm animate-spin"
                data-testid="collapsed-nav-facility-selector-loader"
                aria-label="Loading"
              />
            )}
          </div>
        }
      >
        <DropdownMenuContent
          className={'border-input border-1 min-w-48 !z-modal'}
          side={'right'}
          align={'start'}
          onKeyDown={(e) => e.stopPropagation()}
          data-testid={'collapsed-nav-facility-selector-content'}
          withPortal
        >
          <div className={'m-2.5'}>
            {/* The click guard sits on the group: most of the 36px row is field
                chrome, not the `<input>`, and a press there reaches
                `collapsed-nav.tsx`'s `onClick={toggleExpanded}`. `bg-tertiary` goes on
                `InputField` because `.input-field` paints its own `--input-bg` over
                the group root. */}
            <Input className="input-reserve-clear" onClick={(e) => e.stopPropagation()}>
              <InputField
                className={'bg-tertiary'}
                aria-label={'Search Facilities'}
                placeholder={'Search Facilities'}
                onChange={handleSearch}
                // The DropdownMenu steals typing for its own typeahead.
                onKeyDown={(e) => e.stopPropagation()}
                value={inputValue}
                data-testid={'collapsed-nav-facility-selector-search'}
              >
                <InputLeadingIcon>
                  <Search />
                </InputLeadingIcon>
                {inputValue && (
                  <InputClear aria-label={'Clear search'} onClick={(e) => handleClearSearch(e)} />
                )}
              </InputField>
            </Input>
          </div>
          <DropdownMenuSeparator />
          <div className={'overflow-y-auto max-h-[66vh] scrollbar-hide'}>
            {filteredOrgFacilityList?.map((org, index) => {
              const offlineFacilityCount = getOfflineFacilityCountByOrg(org.organizationId);
              const OrgLogoIcon = OrgLogos[org.iconName as OrgLogoId];

              return (
                <React.Fragment key={org.organizationId}>
                  <DropdownMenuLabel
                    className={'text-muted-foreground uppercase flex justify-start text-xs'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {OrgLogoIcon && (
                      <OrgLogoIcon size={'1rem'} className={'mr-1'} color={'white'} />
                    )}
                    <div>{org.displayName}</div>
                    {offlineFacilityCount > 0 && (
                      <NotificationBadge
                        className={'bg-warning w-3.5 h-3.5 text-black ml-auto text-2xs'}
                        count={offlineFacilityCount}
                      />
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {(org.facilities || []).map((item) => (
                      <DropdownMenuItem
                        key={item.facilityId}
                        onClick={(e) => handleSelect(e, org.name, item.shortName)}
                        className={cn(
                          'cursor-pointer',
                          item.facilityId === currentFacilityId &&
                            'bg-primary/20 text-primary hover:bg-primary/30'
                        )}
                        data-testid={`collapsed-nav-facility-selector-item-${item.facilityId}`}
                      >
                        <FacilityNavItem
                          displayName={item.displayName}
                          isOffline={offlineFacilities.has(item.facilityId)}
                        />
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                  {index < (filteredOrgFacilityList?.length ?? 0) - 1 && <DropdownMenuSeparator />}
                </React.Fragment>
              );
            })}
            {filteredOrgFacilityList?.length === 0 && (
              <DropdownMenuItem className={'text-muted-foreground'}>
                {'No matching facilities found.'}
              </DropdownMenuItem>
            )}
          </div>
        </DropdownMenuContent>
      </NavTooltipDropdownTrigger>
    </div>
  );
};

const FacilityNavItem = ({
  displayName,
  isOffline,
}: {
  displayName: string;
  isOffline: boolean;
}) => {
  return (
    <div className={'flex items-center justify-between w-full'}>
      <span className={cn('truncate', isOffline ? 'text-warning' : '')}>{displayName}</span>
      {isOffline && <CloudOff className="glyph glyph-sm ml-2 text-warning" aria-hidden="true" />}
    </div>
  );
};
